"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, CircleCheck as CheckCircle2, Compass, HeartHandshake, Info, Layers, Search, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
const dogTraits = [{
  title: "Energy & Exercise Index",
  desc: "Targeted daily exertion and mental enrichment hours for working, sporting, and companion groups.",
  icon: Activity
}, {
  title: "Coat Care & Shedding",
  desc: "Undercoat maintenance frequency, hypoallergenic factors, and clinical dermatological needs.",
  icon: Sparkles
}, {
  title: "Temperament & Social Profile",
  desc: "Biddability, prey drive thresholds, and multi-pet household compatibility indicators.",
  icon: HeartHandshake
}, {
  title: "Genetic Health & Longevity",
  desc: "Breed-specific health screening protocols, orthopaedic risks, and median lifespan distributions.",
  icon: ShieldCheck
}];
const catTraits = [{
  title: "Vocalization & Sociability",
  desc: "Interaction demands, independence baselines, and vocal engagement ratings by lineage.",
  icon: HeartHandshake
}, {
  title: "Grooming & Coat Density",
  desc: "Hairball prevention, bristle density, and matting risks for longhair, rex, and shorthair types.",
  icon: Sparkles
}, {
  title: "Activity & Vertical Territory",
  desc: "Play motivation, spatial enrichment requirements, and indoor stimulation thresholds.",
  icon: Activity
}, {
  title: "Feline Health Markers",
  desc: "Predispositions to renal, cardiac (HCM), and joint conditions across recognized breeds.",
  icon: ShieldCheck
}];
const quickFilters = [{
  label: "All Breeds",
  to: "#all"
}, {
  label: "Toy & Compact",
  to: "#compact"
}, {
  label: "Medium & Versatile",
  to: "#medium"
}, {
  label: "Large & Athletic",
  to: "#large"
}, {
  label: "Hypoallergenic",
  to: "#hypoallergenic"
}];
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const [activeSpecies, setActiveSpecies] = useState("dogs");
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="intro" data-nav="light" className="relative bg-background text-foreground pt-12 pb-16 md:pt-16 md:pb-24 border-b border-border overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeInVariants} className="max-w-3xl mb-12">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-foreground text-xs font-semibold uppercase tracking-wider mb-5">
            <Compass className="size-3.5 text-foreground" />
            <span>Comprehensive Breed Profiles</span>
          </div>

          {/* Exact dictated headline in editorial serif Fraunces */}
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.18] mb-5">
            Explore Dog & Cat Breeds
          </h1>

          {/* Clear, authoritative supporting copy */}
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6 font-default">
            Review physical characteristics, daily activity demands, temperament traits, and care considerations for purebred and mixed-breed dogs and cats. Every profile is structured to help you make evidence-informed decisions for your household.
          </p>

          {/* Clinical Governance Callout Motif */}
          <div className="border-l-4 border-foreground bg-card border-y border-r border-border rounded-r-lg p-4 sm:p-5 flex items-start gap-3.5 shadow-xs">
            <Info className="size-5 text-foreground shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed text-card-foreground">
              <span className="font-semibold text-foreground">Standardized Evaluation Methodology:</span> All physical sizing, energy tiers, and health screening criteria align with published veterinary reference metrics and recognized registry breed standards.
            </div>
          </div>
        </motion.div>

        {/* Dual Tab Filtering and Exploration Matrix */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeInVariants} className="bg-card border border-border rounded-xl p-5 sm:p-8 shadow-xs">
          <Tabs defaultValue="dogs" onValueChange={setActiveSpecies} className="w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border">
              <div>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                  Directory Filter
                </span>
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                  Select Species Reference
                </h2>
              </div>

              {/* Tab Selector */}
              <TabsList className="bg-muted p-1 rounded-lg border border-border self-start md:self-auto h-auto">
                <TabsTrigger value="dogs" className="px-5 py-2 text-sm font-semibold rounded-md data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs transition-[color,box-shadow]">
                  Canine Breeds
                </TabsTrigger>
                <TabsTrigger value="cats" className="px-5 py-2 text-sm font-semibold rounded-md data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs transition-[color,box-shadow]">
                  Feline Breeds
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Canine Content Panel */}
            <TabsContent value="dogs" className="mt-6 focus-visible:outline-none">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                {/* Visual Feature Card */}
                <div className="lg:col-span-5 relative">
                  <div className="relative rounded-xl overflow-hidden border border-border bg-muted shadow-sm aspect-4/3 sm:aspect-16/10 lg:aspect-4/3">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/canine_breed_spotlight.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/canine_breed_spotlight.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/canine_breed_spotlight.png-wvc-sizes" alt="Golden retriever dog sitting calmly in an outdoor natural environment" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute bottom-4 left-4 right-4 text-background">
                      <span className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-background/20 backdrop-blur-xs text-background mb-1 inline-block">
                        Canine Directory
                      </span>
                      <h3 className="font-serif text-lg font-medium text-background">
                        220+ Verified Dog Breed Profiles
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Trait Matrix Grid */}
                <div className="lg:col-span-7 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {dogTraits.map((trait, i) => {
                    const IconComp = trait.icon;
                    return <div key={trait.title} data-index={i} className="p-4 rounded-lg bg-background border border-border hover:border-foreground/30 transition-[border-color,box-shadow] duration-200">
                          <div className="flex items-center gap-2.5 mb-2">
                            <div className="size-7 rounded-md bg-muted flex items-center justify-center text-foreground shrink-0">
                              <IconComp className="size-4" />
                            </div>
                            <h4 className="font-semibold text-sm text-foreground">
                              {trait.title}
                            </h4>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {trait.desc}
                          </p>
                        </div>;
                  })}
                  </div>

                  {/* Actions & Category jump links */}
                  <div className="pt-2 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-muted-foreground mr-1">Quick Browse:</span>
                      {quickFilters.map((filter, i) => <Link key={filter.label} to={filter.to} data-index={i} className="text-xs px-2.5 py-1 rounded-md bg-muted text-foreground hover:bg-foreground hover:text-background transition-colors">
                          {filter.label}
                        </Link>)}
                    </div>

                    <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                      <Link to="/DogsPage">
                        <span>Browse All Dogs</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Feline Content Panel */}
            <TabsContent value="cats" className="mt-6 focus-visible:outline-none">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                {/* Visual Feature Card */}
                <div className="lg:col-span-5 relative">
                  <div className="relative rounded-xl overflow-hidden border border-border bg-muted shadow-sm aspect-4/3 sm:aspect-16/10 lg:aspect-4/3">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/feline_breed_spotlight.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/feline_breed_spotlight.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/feline_breed_spotlight.png-wvc-sizes" alt="Short-haired domestic cat resting comfortably on an editorial perch" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute bottom-4 left-4 right-4 text-background">
                      <span className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-background/20 backdrop-blur-xs text-background mb-1 inline-block">
                        Feline Directory
                      </span>
                      <h3 className="font-serif text-lg font-medium text-background">
                        75+ Verified Cat Breed Profiles
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Trait Matrix Grid */}
                <div className="lg:col-span-7 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {catTraits.map((trait, i) => {
                    const IconComp = trait.icon;
                    return <div key={trait.title} data-index={i} className="p-4 rounded-lg bg-background border border-border hover:border-foreground/30 transition-[border-color,box-shadow] duration-200">
                          <div className="flex items-center gap-2.5 mb-2">
                            <div className="size-7 rounded-md bg-muted flex items-center justify-center text-foreground shrink-0">
                              <IconComp className="size-4" />
                            </div>
                            <h4 className="font-semibold text-sm text-foreground">
                              {trait.title}
                            </h4>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {trait.desc}
                          </p>
                        </div>;
                  })}
                  </div>

                  {/* Actions & Category jump links */}
                  <div className="pt-2 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-muted-foreground mr-1">Quick Browse:</span>
                      {quickFilters.map((filter, i) => <Link key={filter.label} to={filter.to} data-index={i} className="text-xs px-2.5 py-1 rounded-md bg-muted text-foreground hover:bg-foreground hover:text-background transition-colors">
                          {filter.label}
                        </Link>)}
                    </div>

                    <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                      <Link to="/CatsPage">
                        <span>Browse All Cats</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* Three Value Benchmarks */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-border/70">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-foreground">Veterinary Reviewed</p>
              <p className="text-xs text-muted-foreground">Screening guidelines validated against current clinical literature.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Zap className="size-4 text-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-foreground">Activity & Space Metrics</p>
              <p className="text-xs text-muted-foreground">Concrete exercise hours and apartment suitability scores.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Layers className="size-4 text-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-foreground">Purebred & Mixed Lineages</p>
              <p className="text-xs text-muted-foreground">Consistent criteria for pedigree and cross-breed companions.</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}